import React, { useCallback, useEffect, useState } from "react";
import { CgDanger, CgCheckO, CgBlock } from "react-icons/cg";
import { FaEdit } from "react-icons/fa";
import { IoAddCircleOutline } from "react-icons/io5";
import { PageListRef, PageList } from "../../../components/PageList";
import { ITable } from "../../../components/TableList";
import { useApi } from "../../../hooks/api";
import { useGlobal } from "../../../hooks/global";
import { IFilters, IStaysReservationProps, IActions, IBusinessProps } from "../../../interfaces";
import { setUpConsultation } from "../../../services/generalServices";
import { secondary } from "../../../theme";
import { getFilterFields } from "../../../utils/filterFields";
import { get_corrrect_names, checkInStatusOptions, reservationStatusOptions } from "../../../utils/options";
import { ModalBusiness } from "../../../components/Modals/ModalBusiness";
import { getLogo, getStatus } from "../../../utils/columnsTables";
import { subscription_status } from "../../../utils/reservation_status";
import { formatDateNoTime } from "../../../utils/formatters";

export const checkInStatusMapper: { [key: string]: { text: string; color: string, icon: React.ReactElement<any> } } = {
  pending: { text: "Pendente", color: '#FAAD14', icon: <CgDanger size={19} style={{ marginRight: 3 }} /> },
  done: { text: "Realizado", color: '#52C41A', icon: <CgCheckO size={16} style={{ marginRight: 3 }} /> },
  late: { text: "Em atraso", color: '#FF6B6B', icon: <CgBlock size={19} style={{ marginRight: 3 }} /> },
  not_realized: { text: "Não realizado", color: '#FF6B6B', icon: <CgBlock size={16} style={{ marginRight: 3 }} /> },
}

const initialFilters: IFilters[] = [
  getFilterFields("state_id"),
  getFilterFields("city_id"),
  getFilterFields("plan_id"),
  getFilterFields("business_trust"),
  getFilterFields("business_status"),
  getFilterFields("plan_status"),
];

const initialTable: ITable = {
  data: [],
  columns: [
    {
      attr: "logo",
      name: "Logo",
      style: { textAlign: "center" },
      styleContent: { fontSize: 16, fontWeight: 600, textAlign: "center" },
    },
    {
      attr: "trade_name",
      name: "Nome da empresa",
      style: { textAlign: "center" },
      styleContent: { fontSize: 16, fontWeight: 600, textAlign: "center" },
    },
    {
      attr: "created_at",
      name: "Data de cadastro",
      style: { textAlign: "center" },
      styleContent: { fontSize: 16, fontWeight: 600, textAlign: "center" },
    },
    {
      attr: "subscription_status",
      name: "Status do plano",
      style: { textAlign: "center" },
      styleContent: { fontSize: 14 },
    },
    {
      attr: "plan_name",
      name: "Plano",
      style: { textAlign: "center" },
      styleContent: { fontSize: 14, textAlign: "center" },
    },
    {
      attr: "count_accommodations",
      name: "Acomodações",
      style: { textAlign: "center" },
      styleContent: { fontSize: 14, textAlign: "center" },
    },
    {
      attr: "count_users",
      name: "Usuários",
      style: { textAlign: "center" },
      styleContent: { fontSize: 14, textAlign: "center" },
    },
  ],
};

export const propertyInExtension = (address: any) => {
  if (!address) return "-";
  return `${address?.street}, ${address?.region}, Nº ${address?.streetNumber}, ${address?.city} - ${address?.stateCode}`;
};

export const BusinessListRoot: React.FC = () => {
  const { user, api } = useApi();
  const { theme, paginationLimit, notify } = useGlobal();

  const [businessIdSelected, setBusinessIdSelected] = useState<string | null>(null)

  const [atualPage, setAtualPage] = useState(1);
  const [filters, setFilters] = useState<any>({});
  const [openModal, setOpenModal] = useState(false);
  const [numOfReservations, setNumOfReservations] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(new Date());
  const [loadingReservations, setLoadingReservations] = useState(true);
  const [searchString, setSearchString] = useState<string>("");
  const [numberOfPages, setNumberOfPages] = useState<number>(1);
  const [reservationSelected, setReservationSelected] = useState<IStaysReservationProps | null>(null);
  const [table, setTable] = useState<ITable>(initialTable);
  const page_ref = React.useRef<PageListRef>(null);

  const selectReservation = useCallback(
    (accomoddation: IStaysReservationProps) => {
      setReservationSelected({ ...accomoddation });
      setOpenModal(true);
    },
    []
  );

  const searchBusiness = useCallback(
    async (page, _filters = null) => {
      setAtualPage(page);
      setLoadingReservations(true);

      try {
        const filters_obj = _filters ? _filters : { ...filters, search: searchString };

        let string = setUpConsultation(filters_obj);
        let url = `/business/?page=${page}&limit=${paginationLimit}&${string}`;

        const result = await api.get(url);
        const new_business = result.data.data as IBusinessProps[];

        setTable((atual) => {
          const new_table = { ...atual };

          new_table.data = [];
          new_business.forEach((business) => {

            new_table.data.push({
              id: business.id,
              created_at: formatDateNoTime(business?.created_at),
              plan_name: business?.plan_name,
              trade_name: business?.trade_name,
              count_accommodations: business?.count_accommodations,
              count_users: business?.count_users,
              logo: getLogo({
                logo: business?.logo,
                theme,
              }),
              subscription_status: getStatus({
                ...subscription_status[business.subscription_status],
                theme,
              }),
              this: business,
            });
          });

          return { ...new_table };
        });
        setNumOfReservations(result.data.count);
        setNumberOfPages(Math.ceil(result.data.count / paginationLimit));
        setUpdatedAt(new Date());
      } catch (err) {
        notify("Erro na busca pelas Empresas!", "error");
      }
      setLoadingReservations(false);
    },
    [user, filters, searchString, businessIdSelected]
  );

  const changeTrustBusiness = useCallback(
    async (item: IBusinessProps) => {
      const newTrustValue = !item?.trust;

      setTable((atual) => {
        const new_table = { ...atual };
        const pos = new_table.data.findIndex((d) => d?.this.id === item?.id);
        if (pos !== -1) new_table.data[pos].this.trust = newTrustValue;
        return { ...new_table };
      });

      try {
        await api.put(`/business/${item?.id}`, { trust: newTrustValue });
        notify(
          `Confiança da empresa ${newTrustValue ? "ativada" : "desativada"} com sucesso`,
          "success"
        );
      } catch (err) {
        notify("Erro ao alterar confiança da empresa", "error");
        setTable((atual) => {
          const new_table = { ...atual };
          const pos = new_table.data.findIndex((d) => d?.this.id === item?.id);
          if (pos !== -1) new_table.data[pos].this.trust = !newTrustValue;
          return { ...new_table };
        });
      }
    },
    []
  );

  const changeStatusBusiness = useCallback(
    async (busId: string, old_status: boolean) => {
      const new_active = !old_status;

      setTable((atual) => {
        const new_table = { ...atual };
        const pos = new_table.data.findIndex((d) => d?.this.id === busId);
        if (pos !== -1) new_table.data[pos].this.active = new_active;
        return { ...new_table };
      });

      try {
        await api.put(`/business/${busId}`, { active: new_active });
        notify("Status da empresa atualizado com sucesso", "success");
      } catch (err) {
        notify("Erro ao alterar status da empresa", "error");
        setTable((atual) => {
          const new_table = { ...atual };
          const pos = new_table.data.findIndex((d) => d?.this.id === busId);
          if (pos !== -1) new_table.data[pos].this.active = old_status;
          return { ...new_table };
        });
      }
    },
    []
  );

  const openModalNewReservation = useCallback(() => {
    setReservationSelected(null);
    setOpenModal(true);
  }, []);

  const handleAction = (action_id: string) => {
    if (action_id === "new_item") openModalNewReservation();
  };

  const actionsInitial: IActions[] = [
    {
      main_option: { text: "Nova Empresa", id: "new_item" },
      className: "new_item",
      icon: <IoAddCircleOutline size={20} className="new_item" />,
      options: [],
      type: "solid",
      permissions: ["ROOT"],
    },
  ];

  const listAction = [
    {
      title: "Editar",
      onClick: (reservation: IStaysReservationProps) => selectReservation(reservation),
      icon: <FaEdit color={secondary[theme]} size={20} />,
      disabled: () => false,
    },
  ];

  const onSaveReservation = useCallback(() => {
    setOpenModal(false);
    searchBusiness(1);
  }, [user, filters, searchString]);

  useEffect(() => {
    const query = window.location.search.split("?").pop()
    const queries = query ? query.split("&") : [""]
    let status: any = queries.find((item: string) => item.split("=")[0] === "status")
    status = status ? status.split("=")[1] : null
    if (status) {
      setFilters({ check_in_status: status, reservation_status: 'booked' })

      page_ref.current?.setFilters((prev) => {
        const checkInStatusFilterIndex = prev.findIndex((filter) => filter.key === "check_in_status" as any)
        const reservationStatusFilterIndex = prev.findIndex((filter) => filter.key === "reservation_status" as any)

        if (checkInStatusFilterIndex !== -1) {
          const mapper = get_corrrect_names(checkInStatusOptions);

          prev[checkInStatusFilterIndex] = { ...prev[checkInStatusFilterIndex], value: { label: mapper[status], value: status } }
        }

        if (reservationStatusFilterIndex !== -1) {
          const mapper = get_corrrect_names(reservationStatusOptions);
          prev[reservationStatusFilterIndex] = { ...prev[reservationStatusFilterIndex], value: { label: mapper['booked'], value: 'booked' } }
        }

        return prev
      })

      searchBusiness(1, { check_in_status: status, reservation_status: 'booked' })
    }
    else searchBusiness(1)
  }, [businessIdSelected]);

  return (
    <PageList
      updatedAt={updatedAt}
      loading={loadingReservations}
      setLoading={setLoadingReservations}
      numberOfItems={numOfReservations}
      onClickOption={handleAction}
      textHeader="Empresas"
      textItems="empresas encontradas"
      text_loading={"Buscando Empresas"}
      text_empty={"Nenhuma empresa encontrada"}
      theme={theme}
      actions={actionsInitial}
      initialFilters={initialFilters}
      onChangeFilters={setFilters}
      table={table}
      pages={numberOfPages}
      page={atualPage}
      search={searchBusiness}
      onChangeSearch={setSearchString}
      listActions={listAction}
      ref={page_ref}
      have_status={true}
      status_action={changeStatusBusiness}
      switch_action={changeTrustBusiness}
      have_switch={true}
      switch_checked={"trust"}
      switchText="CONFIANÇA"
    >
      {openModal && (
        <ModalBusiness
          onCancel={() => setOpenModal(false)}
          onSave={onSaveReservation}
          openModal={openModal}
          backgroundVisible={false}
          defaultId={reservationSelected?.id}
        />
      )}

    </PageList>
  );
};
